import axios, { AxiosError, AxiosRequestConfig } from "axios";

import { customerTokenService } from "@/services/customer-auth";
import { pinSession } from "@/utils/pinSession";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

// ============================================
// Axios Instance
// withCredentials: true → trình duyệt TỰ ĐỘNG gửi/nhận HttpOnly cookies (admin/staff)
// Request interceptor bên dưới sẽ attach Bearer token cho customer auth
// ============================================
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// ============================================
// Request Interceptor
// Đọc customer_access_token từ localStorage và gắn vào Authorization header.
// Nếu là endpoint refresh → dùng refresh_token thay thế.
// Nếu không có token (staff/admin dùng cookie), bỏ qua.
// ============================================
api.interceptors.request.use(
  (config) => {
    const isCustomerRefresh =
      config.url?.includes("/auth-customer/refresh") ||
      config.url?.includes("/auth/refresh");

    // Get appropriate token based on endpoint (reads from localStorage or auto-hydrates from Cookies)
    const token = isCustomerRefresh
      ? customerTokenService.getRefreshToken()
      : customerTokenService.getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ============================================
// Refresh Token Queue Pattern
// Khi accessToken hết hạn (15 phút), nhiều request có thể fail 401 cùng lúc.
// Queue đảm bảo CHỈ GỌI REFRESH 1 LẦN, các request khác chờ kết quả.
// ============================================

let isRefreshing = false;

interface QueueItem {
  resolve: (value?: unknown) => void;
  reject: (reason: unknown) => void;
}
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  });
  failedQueue = [];
};

// Extend AxiosRequestConfig to include retry flag
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// ============================================
// Response Interceptor - Auto Refresh on 401 & PIN session renew
// ============================================
api.interceptors.response.use(
  (response) => {
    // Gia hạn PIN session mỗi khi có API call thành công
    if (pinSession.isValid()) {
      pinSession.renew();
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Skip refresh for auth endpoints to avoid infinite loops
    const isAuthEndpoint =
      originalRequest.url?.includes("/auth-customer/login") ||
      originalRequest.url?.includes("/auth-customer/refresh") ||
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth-customer/register") ||
      originalRequest.url?.includes("/auth-customer/otp/") ||
      originalRequest.url?.includes("/auth/store/login") ||
      originalRequest.url?.includes("/auth/store/refresh") ||
      originalRequest.url?.includes("/auth/store/logout");

    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    // Handle 403 - Forbidden (Manager PIN invalid/revoked)
    if (error.response?.status === 403) {
      if (customerTokenService.getType() === "store") {
        pinSession.clear();
        window.location.href = "/pos";
      } else {
        window.dispatchEvent(new CustomEvent("auth:logout"));
      }
      return Promise.reject(error);
    }

    // Handle 401 - Access Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      const authType = customerTokenService.getType();
      const refreshToken = customerTokenService.getRefreshToken();

      // If user is not authenticated at all, skip refresh and just reject
      if (!authType && !refreshToken) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request and wait
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Phân biệt loại auth để gọi đúng refresh endpoint
        if (authType === "store") {
          // Staff/Admin: dùng HttpOnly cookie, browser tự gửi
          await axios.post(
            `${API_BASE_URL}/auth/store/refresh`,
            {},
            { withCredentials: true },
          );
        } else {
          // Customer: dùng refresh_token từ localStorage
          if (!refreshToken) {
            throw new Error("No refresh token available");
          }
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {
              refresh_token: refreshToken,
            },
            { withCredentials: true },
          );
          customerTokenService.setAccessToken(response.data.access_token);
        }

        // Refresh thành công!
        processQueue(null);

        // Retry request gốc
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh thất bại → reject all queued requests
        processQueue(refreshError);

        // Dọn dẹp token & cookie khi phiên làm việc hết hạn
        customerTokenService.clearAuth();

        // Dispatch logout event để ứng dụng dọn dẹp state
        window.dispatchEvent(new CustomEvent("auth:logout"));

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;
